/**
 * Pure server-name resolution for device-scoped camera-model actions.
 *
 * A capture-device action (discovery, screenshot capture) must be routed
 * to an inference machine, but the hostname lives on *cameras* — and the device
 * being acted on may not have any camera with one yet. This helper encodes the
 * fallback chain once, so the disabling reason of a button and the banner of a
 * panel are computed by the same code and cannot diverge.
 *
 * Resolution chain:
 *   1. hostname of any camera of the device        → { source: "device" }
 *   2. hostname of any other camera of the store    → { source: "store" }
 *   3. nothing resolvable                           → serverName null + reason
 *
 * `useInferenceMachineCache` resolves a failed or pending lookup to a `#<id>`
 * placeholder (both in its `.then` and its `.catch`). That string is display-only:
 * sent as a hostname it would turn the end of the URL into a fragment. Every step
 * therefore treats it as unresolved — along with anything outside a strict
 * hostname allowlist (`[A-Za-z0-9._-]`, no `.`/`..`), since the value travels
 * raw in a URL path segment.
 */

/**
 * Resolve a camera's inference-machine hostname; a `#<id>` placeholder — or any
 * value that could not travel safely in a URL path — counts as unresolved.
 *
 * @param {object} camera - A camera node from the store tree.
 * @param {Object<number, string>} machineCache - Map from inference-machine id to hostname.
 * @returns {string|null} The hostname, or null when unresolved.
 */
function resolvedHostname(camera, machineCache) {
  if (camera?.inference_machine == null) return null;
  const host = String((machineCache || {})[camera.inference_machine] ?? "").trim();
  // Allowlist, not blocklist: the hostname travels raw in a URL path segment.
  return /^[A-Za-z0-9._-]+$/.test(host) && host !== "." && host !== ".." ? host : null;
}

/**
 * Resolve the inference-machine hostname to use for an action on a capture device.
 *
 * @param {object} device - The capture device node (with its `cameras` array).
 * @param {Array<object>} tree - The full store tree (devices → cameras), used as fallback.
 * @param {Object<number, string>} machineCache - Map from inference-machine id to hostname,
 *   as returned by `useInferenceMachineCache`.
 * @returns {{serverName: string|null, source: "device"|"store"|null, reason: string|null}}
 *   `source` says where the hostname came from ("store" means another device's camera —
 *   worth an info banner); when nothing is resolvable, `serverName` is null and `reason`
 *   is a displayable explanation.
 */
export default function resolveDeviceServerName(device, tree, machineCache) {
  const fromDevice = (device?.cameras || []).map((camera) => resolvedHostname(camera, machineCache)).find(Boolean);
  if (fromDevice) return { serverName: fromDevice, source: "device", reason: null };

  const fromStore = (tree || [])
    .flatMap((d) => d?.cameras || [])
    .map((camera) => resolvedHostname(camera, machineCache))
    .find(Boolean);
  if (fromStore) return { serverName: fromStore, source: "store", reason: null };

  return {
    serverName: null,
    source: null,
    reason: "No camera in this store has a resolved inference machine yet.",
  };
}
